import * as $ from "jquery";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import {BaseSensorConfigView, BaseSensorConfigViewOptions} from "./_BaseSensorConfigView";
import {BacnetOperationView, UIChannel} from "./BacnetOperation";
import {ModuleCollection} from "../../../collections/Modules";
import {ActionPacketCollection} from "../../../collections/ActionPackets";
import {BacnetDiscoveryCollection, BacnetDiscoveryRecord} from "../../../collections/BacnetDiscoveries";
import {BacnetPropertyCollection, BacnetPropertyRecord} from "../../../collections/BacnetProperties";
import {BacnetUnitCollection, BacnetUnitRecord} from "../../../collections/BacnetUnits";
import {SensorRecord} from "../../../models/Module";

var bacnetTemplate = require('templates/settings/sensor_config/bacnet.html');

const BACNET_POSITION_MULTIPLIER = 100000;
const BACNET_FIRST_POSITION = 100000;
const BACNET_SECTIONS_PER_PACKET = (9 * 3 + 4);
const DISCOVERY_POLL_INTERVAL = 15000;

interface LookupTableEntry {
    macAddress: string;
    networkNumber: number;
    deviceAddress: number;
}

interface ConfigurationSettings {
    PARITY?: number;
    BAUD_RATE?: number;
    DEV_ADDR?: number;
    MAX_MASTER?: number;
    DEV_INSTANCE?: number;
    MAX_INFO_FRAMES?: number;
    REPLY_TIMEOUT?: number;
    USAGE_TIMEOUT?: number;
    POLARITY?: number;
}

interface ConfigurationBlock {
    macAddress: string;
    networkNumber: number;
    deviceAddress: number;
    property: number;
    objectId: number;
    position: number;
}

interface ProcessedConfiguration {
    settings: ConfigurationSettings;
    blocks: ConfigurationBlock[]
}

interface DiscoveredProperty {
    name: string;
    number: number;
    value: number;
}

interface DiscoveredObject {
    name: string;
    objectId: number;
    properties: DiscoveredProperty[],
    uniqueProperties: DiscoveredProperty[]
}

export interface ProcessedDeviceDiscovery {
    vendor: string;
    name: string;
    model: string;
    addr: string;
    objects: { [key: string]: DiscoveredObject }
}

export class BacnetConfigView extends BaseSensorConfigView {

    views: BacnetOperationView[] = [];
    moduleCollection: ModuleCollection;
    actionPacketCollection: ActionPacketCollection;
    discoveryCollection: BacnetDiscoveryCollection;
    bacnetProps: { [key: number]: BacnetPropertyRecord };
    bacnetUnits: { [key: number]: BacnetUnitRecord };

    processedConfig: ProcessedConfiguration;
    cachedUIProcessedConfig: ProcessedConfiguration;
    processedDiscovery: { [key: string]: ProcessedDeviceDiscovery };
    cachedUIChannels: { [key: string]: UIChannel };

    collectionLoaded = false;
    startDiscoveryClicked = false;
    startDiscoveryReported = false;
    endDiscoveryReported = false;
    discoveryProgress = 0;
    lastDiscoveryDate = null;
    maxOpId = 0;

    template = _.template(bacnetTemplate);

    constructor(options: BaseSensorConfigViewOptions) {
        super(_.extend({
            events: {
                "click .settings-sensorconfig-bacnet-add-operation": 'handleClickAddOperation',
                "click .settings-sensorconfig-bacnet-start-discovery": 'handleStartDiscovery'
            }
        }, options));

        this.bacnetProps = <{ [key: number]: BacnetPropertyRecord }> _.keyBy((new BacnetPropertyCollection()).toJSON(), 'number');
        this.bacnetUnits = <{ [key: number]: BacnetUnitRecord }> _.keyBy((new BacnetUnitCollection()).toJSON(), 'number');
        this.discoveryCollection = new BacnetDiscoveryCollection([], {sensorId: this.sensorId});
        this.actionPacketCollection = new ActionPacketCollection([], {installId: options.installId});
        this.moduleCollection = new ModuleCollection([], {installId: options.installId});

        //array of important fetches
        var deferreds = [
            this.discoveryCollection.fetch(),
            this.moduleCollection.fetch()
        ];
        //check for ovrd permissions before fetching action packets
        if (this.permissionCollection.canViewInstallOverrides(this.installId)) {
            deferreds.push(this.actionPacketCollection.fetch());
        }

        $.when.apply(this, deferreds).done(() => {
            this.collectionLoaded = true;

            // Check if discovery is currently active
            this.startDiscoveryReported = this.discoveryInProgressReported();
            if (this.startDiscoveryReported) {
                setTimeout(() => {
                    this.waitForEndDiscovery()
                }, DISCOVERY_POLL_INTERVAL);
            }

            this.processedDiscovery = null;
            this.processDiscovery();
            this.createOperationViews(true);
            this.render();
        });

        // Bring the configuration into a uniform format whether it is Sensor Version 1 or Sensor Version 2
        this.processedConfig = this.con.sensor_version === 1 ? this.processV1Config(this.config) : this.processV2Config(this.config);

        this.views = [];

        // These fields will be populated when saving
        this.cachedUIChannels = null;
        this.cachedUIProcessedConfig = null;
    }

    processV1Config(config) {
        var settings = {};
        var blocks = [];

        var currentPosition = BACNET_FIRST_POSITION;
        var currentBlock = null;
        config.each((c) => {
            var configType = c.get('type');
            var configValue = c.get('value');

            switch (configType) {
                // Process Settings (Instruction = 0)
                case 'PARITY':
                case 'BAUD_RATE':
                case 'DEV_ADDR':
                case 'MAX_MASTER':
                case 'DEV_INSTANCE':
                case 'MAX_INFO_FRAMES':
                case 'REPLY_TIMEOUT':
                case 'USAGE_TIMEOUT':
                case 'POLARITY':
                    settings[configType] = configValue;
                    break;

                // Process Blocks
                case 'ADDR':
                    currentBlock = {
                        macAddress: null,
                        networkNumber: null,
                        deviceAddress: configValue,
                        property: null,
                        objectId: null,
                        position: currentPosition
                    };
                    break;
                case 'PROPERTY':
                    currentBlock['property'] = configValue;
                    break;
                case 'OBJECTID':
                    currentBlock['objectId'] = configValue;
                    blocks.push(currentBlock);
                    currentPosition += BACNET_POSITION_MULTIPLIER;
                    break;
            }
        });

        return {
            settings: settings,
            blocks: blocks
        }
    }

    processV2Config(config) {
        var settings = {};
        var blocks = [];

        var currentLookup = null;
        var lookUpTable = [];

        var currentPosition = BACNET_FIRST_POSITION;
        var currentBlock = null;
        config.each(function (c) {
            var configType = c.get('type');
            var configValue = c.get('value');

            switch (configType) {
                // Process Settings (Instruction = 0)
                case 'PARITY':
                case 'BAUD_RATE':
                case 'DEV_ADDR':
                case 'MAX_MASTER':
                case 'DEV_INSTANCE':
                case 'MAX_INFO_FRAMES':
                case 'REPLY_TIMEOUT':
                case 'USAGE_TIMEOUT':
                case 'POLARITY':
                    settings[configType] = configValue;
                    break;

                // Process Lookup Table
                case 'LT_ADDR':
                    currentLookup = {deviceAddress: configValue, networkNumber: null, macAddress: null};
                    break;
                case 'LT_NETWORK':
                    currentLookup['networkNumber'] = configValue;
                    break;
                case 'LT_MAC':
                    var str = configValue.toString(16);
                    while (str.length < 12) str = '0' + str;
                    currentLookup['macAddress'] = str;
                    lookUpTable.push(currentLookup);
                    break;

                // Process Blocks
                case 'INDEX':
                    var idx = configValue - 1; // The config lookup table is 1-indexed
                    currentBlock = {
                        macAddress: lookUpTable[idx]['macAddress'],
                        networkNumber: lookUpTable[idx]['networkNumber'],
                        deviceAddress: lookUpTable[idx]['deviceAddress'],
                        property: null,
                        objectId: null,
                        position: currentPosition
                    };
                    break;
                case 'PROPERTY':
                    currentBlock['property'] = configValue;
                    break;
                case 'OBJECTID':
                    currentBlock['objectId'] = configValue;
                    blocks.push(currentBlock);
                    currentPosition += BACNET_POSITION_MULTIPLIER;
                    break;
            }
        }, this);

        return {
            settings: settings,
            blocks: blocks
        }
    }

    prepareForSaving() {
        this.cacheConfigAndChannelsFromUI();
    }

    waitForEndDiscovery() {
        this.moduleCollection.once('sync', () => {
            var discoveryInProgress = this.discoveryInProgressReported();

            if (!this.startDiscoveryReported) {
                this.startDiscoveryReported = discoveryInProgress;
                setTimeout(() => {
                    this.waitForEndDiscovery()
                }, DISCOVERY_POLL_INTERVAL);
            } else {
                this.endDiscoveryReported = !discoveryInProgress;

                if (this.endDiscoveryReported) {
                    this.startDiscoveryClicked = false;
                    this.discoveryCollection.once('sync', function () {
                        this.processedDiscovery = {};
                        this.processDiscovery();
                        this.rerender();
                    }, this);
                    this.discoveryCollection.fetch({reset: true});
                } else {
                    setTimeout(() => {
                        this.waitForEndDiscovery()
                    }, DISCOVERY_POLL_INTERVAL);
                }
            }
        });
        this.moduleCollection.fetch({reset: true});
    }

    getCombinedAddressFromParts(deviceAddress, networkNumber, macAddress) {
        var dev = deviceAddress !== null ? deviceAddress : '';
        var nn = networkNumber !== null ? networkNumber : '';
        var mac = macAddress !== null ? macAddress : '';
        return dev + '!' + nn + '!' + mac;
    }

    getPartsFromCombinedAddress(address) {
        var parts = address.split('!');

        parts[1] = !parts[1].length ? null : Number(parts[1]);
        parts[2] = !parts[2].length ? null : parts[2];
        parts[0] = Number(parts[0]);
        return parts;
    }

    getUniqueUIChannelIdentifier(combinedAddress, objectId, property) {
        return (combinedAddress + '-' + objectId + '-' + property);
    }

    discoveryInProgressReported() {
        var module = this.moduleCollection.findWhere({module_id: this.module.get('module_id')});
        var internalSensor = <SensorRecord> _.find(module.get('sensors'), {sensor_connector: 0});

        var errorTPos = _.find(internalSensor.channels, {channel_position: 11 + (this.con.sensor_connector * 8)});
        var errorPPos = _.find(internalSensor.channels, {channel_position: 12 + (this.con.sensor_connector * 8)});
        var errorVPos = _.find(internalSensor.channels, {channel_position: 13 + (this.con.sensor_connector * 8)});
        if (!errorTPos || !errorPPos || !errorVPos) return false;

        var errorT = errorTPos.channel_converted_value;
        var errorP = errorPPos.channel_converted_value;
        var errorV = errorVPos.channel_converted_value;

        switch (errorT) {
            case 230:
                if (errorV === 100) return true;
                break;
            case 128:
                if(this.con['sensor_firmware_version']==344){
                    var progress = ((errorP << 14) | (errorV >> 2) ) / 4194302;
                } else {
                    var progress = errorP / errorV;
                }
                if (errorV !== 0 && progress > this.discoveryProgress) {
                    this.discoveryProgress = progress;

                    // Bug in jQuery animate where sometimes the percentage is not calculated correctly. Use pixels instead
                    // see https://stackoverflow.com/questions/6877081/jquery-animate-and-property-values-in-percentage
                    var containerWidth = this.$('.settings-sensorconfig-bacnet-discovery-in-progress-tr .progress').width();
                    var width = Math.ceil(containerWidth * progress);
                    this.$('.settings-sensorconfig-bacnet-discovery-progress-bar').animate({width: width}, 2000);
                }
                return errorV !== errorP;
            case 131:
            case 138:
            case 139:
            case 140:
            case 141:
                return true;
        }

        return false;
    }

    processDiscovery() {
        var propsByDevice = {};
        _.each(this.discoveryCollection.toJSON(), (disc: BacnetDiscoveryRecord[]) => {
            var addr = this.getCombinedAddressFromParts(disc['device_address'], disc['network_number'], disc['mac_address']);
            if (_.has(propsByDevice, addr)) {
                propsByDevice[addr].push(disc);
            } else {
                propsByDevice[addr] = [disc];
            }
        });

        var devices: { [key: string]: ProcessedDeviceDiscovery } = {};
        _.each(propsByDevice, (deviceProps: BacnetDiscoveryRecord[], device) => {
            var d = moment(deviceProps[0].created);
            if (this.lastDiscoveryDate === null || d.isAfter(this.lastDiscoveryDate)) this.lastDiscoveryDate = d;

            var propsByObject = _.groupBy(deviceProps, 'object_id');
            devices[device] = {
                vendor: 'Unknown Vendor',
                name: 'Unknown Device',
                model: 'Unknown Model',
                addr: device,
                objects: {}
            };

            _.each(propsByObject, (objectProps, objectId) => {
                var objectType = Number(objectId) >> 22;
                var object = {
                    name: 'Unknown Object',
                    objectId: Number(objectId),
                    properties: [],
                    uniqueProperties: []
                };

                _.each(objectProps, (prop) => {
                    if (objectType === 8) {
                        switch (prop.property_number) {
                            case 70:
                                devices[device]['model'] = prop.str_value;
                                break;
                            case 77:
                                devices[device]['name'] = prop.str_value;
                                break;
                            case 121:
                                devices[device]['vendor'] = prop.str_value;
                                break
                        }
                    }

                    if (prop.property_number === 77) {
                        object.name = prop.str_value;
                    }

                    var existingProp = this.bacnetProps[prop.property_number];
                    var discoveredProp: DiscoveredProperty;
                    if (!existingProp) {
                        discoveredProp = {
                            name: "Property " + prop.property_number,
                            number: prop.property_number,
                            value: prop.value
                        }
                    } else {
                        discoveredProp = {
                            name: existingProp.name,
                            number: existingProp.number,
                            value: prop.value
                        };
                    }
                    object.properties.push(discoveredProp);
                });

                object.uniqueProperties = _.uniqBy(object.properties, 'number');
                devices[device].objects[objectId] = object;
            });
        });

        this.processedDiscovery = devices;
    }

    validate() {
        var subViewValid = true;
        var subViewErrors = [];
        _.each(this.views, function (view) {
            if (view.removed) return;

            var e = view.validateForm();
            subViewErrors.push(e);
            if (subViewValid) subViewValid = !e.length;
        });
        var errors = [];

        // Validate input field limits
        var deviceAddr = Number(this.cachedUIProcessedConfig.settings['DEV_ADDR']);
        if (deviceAddr <= 0 || deviceAddr > 127) errors.push('Bridge Device Address must be between 1 and 127 (both inclusive)');

        var maxMaster = Number(this.cachedUIProcessedConfig.settings['MAX_MASTER']);
        if (maxMaster <= 0 || maxMaster > 127) errors.push('Max Master must be between 1 and 127 (both inclusive)');

        var devInstance = Number(this.cachedUIProcessedConfig.settings['DEV_INSTANCE']) % 4194304;
        if (devInstance < 0 || devInstance > 4194303) errors.push('Device Instance must be between 0 and 4194303 (both inclusive)');

        var replyTimeout = Number(this.cachedUIProcessedConfig.settings['REPLY_TIMEOUT']) + 255;
        if (replyTimeout < 255 || replyTimeout > 300) errors.push('Reply Timeout must be between 255 and 300 (both inclusive)');

        var usageTimeout = Number(this.cachedUIProcessedConfig.settings['USAGE_TIMEOUT']);
        if (usageTimeout < 20 || usageTimeout > 100) errors.push('Usage Timeout must be between 20 and 100 (both inclusive)');

        var infoFrames = Number(this.cachedUIProcessedConfig.settings['MAX_INFO_FRAMES']);
        if (infoFrames < 1 || infoFrames > 50) errors.push('Max info Frames must be between 1 and 1000 (both inclusive)');

        // Check that there are no more than 500 properties configured
        var blocks = this.cachedUIProcessedConfig.blocks;

        var maxops = this.con.sensor_version === 1 ? window['CONFIG'].bacnet.v1.maxops : window['CONFIG'].bacnet.v2.maxops;
        if (blocks.length > maxops) {
            errors.push('Too many operations! You may only configure ' + maxops + ' individual operations');
        }

        if (this.con.sensor_version === 2) {
            // Build a Lookup table
            var lookupTable = [];
            for (var j = 0; j < blocks.length; j++) {
                var block = blocks[j];
                var lookup: any = {
                    macAddress: block.macAddress,
                    networkNumber: block.networkNumber,
                    deviceAddress: block.deviceAddress
                };

                // If the lookup doesn't exist add it
                if (_.findIndex(lookupTable, lookup) === -1) lookupTable.push(lookup);
            }

            if (lookupTable.length > window['CONFIG'].bacnet.v2.maxdevices) {
                errors.push('Too many devices! You may only configure ' + window['CONFIG'].bacnet.v2.maxdevices + ' devices');
            }
        }

        // Check that no addr-obj-property combination was selected twice
        var seen = [];
        for (var i = 0; i < blocks.length; i++) {
            var block = blocks[i];
            var addr = this.getCombinedAddressFromParts(block.deviceAddress, block.networkNumber, block.macAddress);
            var key = this.getUniqueUIChannelIdentifier(addr, block.objectId, block.property);

            if (_.includes(seen, key)) {
                errors.push('Address ' + addr + ' - Object ID ' + block.objectId + ' - ' +
                    'Property ' + block.property + ' was selected multiple times. Please make sure any property ' +
                    'is only selected once.')
            } else {
                seen.push(key);
            }
        }

        // Always rerender to get rid of errors
        this.rerender(errors, subViewErrors);

        return !errors.length && subViewValid;
    }

    hasUpdatedConfig() {
        return true; //JSON.stringify(this.processedConfig) !== this.initialConfig;
    }

    getChannelNameForNewBlock(combinedAddr, objectId, propertyNumber) {
        var propertyName;
        if (_.has(this.bacnetProps, propertyNumber)) {
            propertyName = this.bacnetProps[propertyNumber].name;
        } else {
            propertyName = 'Prop ' + propertyNumber;
        }

        if (_.has(this.processedDiscovery, combinedAddr)) {
            if (_.has(this.processedDiscovery[combinedAddr].objects, objectId)) {
                return this.processedDiscovery[combinedAddr].name + ' - ' +
                    this.processedDiscovery[combinedAddr].objects[objectId].name + ' - ' + propertyName;
            }

            return this.processedDiscovery[combinedAddr].name + ' - ' +
                'ObjId ' + objectId + ' - ' + propertyName;
        }

        return 'Dev ' + combinedAddr + ' - ObjId ' + objectId + ' - ' + propertyName;
    }

    getUnitForBlock(combinedAddr, objectId, propertyNumber) {
        // For now only add a unit when the property is "Present Value"
        if (propertyNumber === 85) {
            if (_.has(this.processedDiscovery, combinedAddr)) {
                if (_.has(this.processedDiscovery[combinedAddr].objects, objectId)) {
                    var unitsProperty = _.find(
                        this.processedDiscovery[combinedAddr].objects[objectId].uniqueProperties, {number: 117}
                    );
                    if (!unitsProperty) return null;

                    var unitModel = this.bacnetUnits[unitsProperty.value];
                    if (unitModel) return unitModel.name;
                }
            }
        }
        return null
    }

    createOperationViews(initial?) {
        this.views = [];
        this.maxOpId = 0;

        // Loop through the processed configuration and add channel properties and group everything by device
        var uiOperationsByDevice = {};
        var blocks = initial ? this.processedConfig.blocks : this.cachedUIProcessedConfig.blocks;

        _.each(blocks, (c) => {
            var channelUnit = '', channelName = '', channelDecimals = null;
            var addr = this.getCombinedAddressFromParts(c.deviceAddress, c.networkNumber, c.macAddress);

            if (initial) {
                var existingChannel = _.find(this.con['channels'], {channel_position: c['position']});
                channelName = existingChannel['channel_name'];
                channelUnit = existingChannel['channel_unit'];
                channelDecimals = existingChannel['channel_decimals'];
            } else {
                var key = this.getUniqueUIChannelIdentifier(addr, c.objectId, c.property);
                if (_.has(this.cachedUIChannels, key)) {
                    channelName = this.cachedUIChannels[key]['name'];
                    channelUnit = this.cachedUIChannels[key]['unit'];
                    channelDecimals = this.cachedUIChannels[key]['decimals'];
                } else {
                    channelName = this.getChannelNameForNewBlock(addr, c.objectId, c.property);
                    channelUnit = this.getUnitForBlock(addr, c.objectId, c.property);
                }
            }

            var deviceOP = {
                property: c['property'],
                objectid: c['objectId'],
                unit: channelUnit,
                channelName: channelName,
                channelDecimals: channelDecimals
            };

            if (_.has(uiOperationsByDevice, addr)) {
                uiOperationsByDevice[addr]['operations'].push(deviceOP);
            } else {
                uiOperationsByDevice[addr] = {
                    device: addr,
                    operations: [deviceOP]
                };
            }
        });
        _.each(uiOperationsByDevice, (uiOps) => {
            this.addNewOperationView(uiOps);
        });
    }

    addNewOperationView(initialOperations?) {
        this.maxOpId++;
        var op = new BacnetOperationView({
            parentView: this,
            opId: this.sensorId + '-' + this.maxOpId,
            opNum: this.maxOpId,
            sensorId: this.sensorId,
            initialOperations: initialOperations,
            discovery: this.processedDiscovery,
            bacnetProps: this.bacnetProps,
            bacnetUnits: this.bacnetUnits,
            permissionCollection: this.permissionCollection,
            installId: this.installId
        });
        this.views.push(op);
        return op;
    }

    handleClickAddOperation() {
        this.$('.settings-sensorconfig-bacnet-operation-container').append(
            this.addNewOperationView().render().el
        );
    }

    handleStartDiscovery() {
        this.discoveryProgress = 0;
        var view = this;

        $.ajax({
            url: this.discoveryCollection.url(),
            type: 'DELETE',
            success: () => {
                this.startDiscoveryClicked = true;
                this.startDiscoveryReported = false;
                this.endDiscoveryReported = false;


                var sensorActionPacket = this.actionPacketCollection.findWhere({
                    sensor_id: this.sensorId,
                    name: 'Discovery Packet'
                });

                var name: any = {name: 'Instruction'};
                var partial = <any> _.find(sensorActionPacket.get('partials'), name);

                var pkt = new this.actionPacketCollection.model({
                    fields: [],
                    partialId: partial.id,
                    createdSeconds: Math.floor((new Date()).getTime() / 1000),
                    validSeconds: 3600,
                    uniqueName: 'Discovery'
                });

                this.actionPacketCollection.add(pkt);
                pkt.save();

                setTimeout(() => {
                    this.waitForEndDiscovery()
                }, DISCOVERY_POLL_INTERVAL);
                this.rerender();
            },
            error: function () {
                this.rerender();
            }
        });
    }

    rerender(errors?, subViewErrors?) {
        this.prepareForSaving();
        this.createOperationViews();
        this.render(errors, subViewErrors, this.cachedUIProcessedConfig.settings);
    }

    render(errors?, subViewErrors?, settings?) {
        errors = errors ? errors : [];
        this.$el.html(this.template({
            settings: settings ? settings : this.processedConfig.settings,
            errors: errors,
            sensorId: this.sensorId,
            discovery: this.processedDiscovery,
            discoveryInProgress: (this.startDiscoveryClicked || (this.startDiscoveryReported && !this.endDiscoveryReported)),
            discoveryCompleted: (this.startDiscoveryReported && this.endDiscoveryReported),
            discoveryProgress: this.discoveryProgress,
            discoveryDate: this.lastDiscoveryDate ? this.lastDiscoveryDate.format('L LTS') : '',
            collectionLoaded: this.collectionLoaded,
            permissionCollection: this.permissionCollection,
            installId: this.installId,
            connector: this.con.sensor_connector,
            con: this.con,
            module: this.module
        }));

        if (this.collectionLoaded) {
            _.each(this.views, (view, i) => {
                this.$('.settings-sensorconfig-bacnet-operation-container').append(
                    view.render(subViewErrors ? subViewErrors[i] : []).el
                );
            });
        }
        return this;
    }

    cacheConfigAndChannelsFromUI() {
        // Get UI channels
        this.cachedUIChannels = {};
        for (var j = 0; j < this.views.length; j++) {
            if (this.views[j].removed) continue;
            this.cachedUIChannels = _.extend(this.cachedUIChannels, this.views[j].getChannelsFromUI());
        }

        // Get Processed Blocks
        var processedBlocks = [];
        for (var j = 0; j < this.views.length; j++) {
            if (this.views[j].removed) continue;
            var subconfig = this.views[j].getConfigFromUI();

            for (var k = 0; k < subconfig.length; k++) {
                var addressComponents = this.getPartsFromCombinedAddress(subconfig[k]['addr']);
                processedBlocks.push({
                    macAddress: addressComponents[2],
                    networkNumber: addressComponents[1],
                    deviceAddress: addressComponents[0],
                    property: subconfig[k]['property'],
                    objectId: subconfig[k]['objectId']
                })
            }
        }

        // Get Settings
        var settings = {
            'PARITY': Number(this.$('.settings-sensorconfig-bacnet-parity').val()),
            'BAUD_RATE': Number(this.$('.settings-sensorconfig-bacnet-baud-rate').val()),
            'DEV_ADDR': Number(this.$('.settings-sensorconfig-bacnet-device-address').val()),
            'MAX_MASTER': Number(this.$('.settings-sensorconfig-bacnet-max-master').val()),
            'DEV_INSTANCE': (8 << 22) | (Number(this.$('.settings-sensorconfig-bacnet-device-instance').val())),
            'MAX_INFO_FRAMES': Number(this.$('.settings-sensorconfig-bacnet-max-info-frames').val()),
            'REPLY_TIMEOUT': Number(this.$('.settings-sensorconfig-bacnet-reply-timeout').val()) - 255,
            'USAGE_TIMEOUT': Number(this.$('.settings-sensorconfig-bacnet-usage-timeout').val()),
            'POLARITY': 0
        };

        this.cachedUIProcessedConfig = {
            settings: settings,
            blocks: processedBlocks
        };
    }

    getPositionForBlock(deviceAddress, networkNumber, macAddress, objectId, property) {
        var block = _.find(this.processedConfig.blocks, {
            deviceAddress: deviceAddress,
            networkNumber: networkNumber,
            macAddress: macAddress,
            objectId: objectId,
            property: property
        });
        return block ? block.position : null;
    }

    getNumberOfPositionsForBlock(deviceAddress, networkNumber, macAddress, objectId, propertyNumber) {
        var addr = this.getCombinedAddressFromParts(deviceAddress, networkNumber, macAddress);
        if (_.has(this.processedDiscovery, addr)) {
            if (_.has(this.processedDiscovery[addr].objects, objectId)) {
                var matchingProps = _.filter(this.processedDiscovery[addr].objects[objectId].properties, function (p) {
                    return p.number === propertyNumber
                });


                return Math.max(matchingProps.length, 1);
            }
        }
        return 1;
    }

    getUpdatedChannels() {
        var channelsToCreate = [];
        var channelsToUpdate = {};
        var positionsToDelete = [];

        // Get all new and update blocks
        var newBlocks = this.cachedUIProcessedConfig.blocks;
        var uiChannels = this.cachedUIChannels;

        for (var i = 0; i < newBlocks.length; i++) {
            var block = newBlocks[i];

            var numPositions = this.getNumberOfPositionsForBlock(
                block.deviceAddress, block.networkNumber, block.macAddress, block.objectId, block.property
            );
            var newPosition = i * BACNET_POSITION_MULTIPLIER + BACNET_FIRST_POSITION;
            var oldPosition = this.getPositionForBlock(
                block.deviceAddress, block.networkNumber, block.macAddress, block.objectId, block.property
            );

            for (var j = 0; j < numPositions; j++) {
                var nameKey = this.getUniqueUIChannelIdentifier(
                    this.getCombinedAddressFromParts(block.deviceAddress, block.networkNumber, block.macAddress),
                    block.objectId, block.property
                );
                var uiChannel = uiChannels[nameKey];

                if (oldPosition === null) {
                    var nameAppendix = j > 0 ? ' (' + (j + 1) + ')' : '';
                    channelsToCreate.push({
                        sensor_id: this.sensorId,
                        name: uiChannel.name + nameAppendix,
                        position: newPosition + j,
                        unit: uiChannel.unit,
                        decimals: uiChannel.decimals,
                        conversion_function_id: null
                    })
                } else {
                    var existingChannel = _.find(this.con['channels'], {channel_position: oldPosition + j});
                    var id = existingChannel['channel_id'];

                    if (oldPosition !== newPosition) {
                        channelsToUpdate[id] = {
                            id: id,
                            position: newPosition + j,
                            oldPosition: oldPosition + j,
                            name: uiChannel.name,
                            unit: uiChannel.unit,
                            decimals: uiChannel.decimals,
                            conversion_function_id: null
                        };
                    } else {
                        channelsToUpdate[id] = {
                            id: id,
                            position: oldPosition + j,
                            name: uiChannel.name,
                            unit: uiChannel.unit,
                            decimals: uiChannel.decimals,
                            conversion_function_id: null
                        };
                    }
                }
            }
        }

        // Get all deleted blocks
        var oldBlocks = this.processedConfig.blocks;
        for (var i = 0; i < oldBlocks.length; i++) {
            var block = oldBlocks[i];

            // Check if the old block does not occur in the new configuration
            var idx = _.findIndex(newBlocks, {
                deviceAddress: block.deviceAddress,
                networkNumber: block.networkNumber,
                macAddress: block.macAddress,
                objectId: block.objectId,
                property: block.property
            });

            if (idx === -1) {
                var numPositions = this.getNumberOfPositionsForBlock(
                    block.deviceAddress, block.networkNumber, block.macAddress, block.objectId, block.property
                );

                for (var j = 0; j < numPositions; j++) {
                    positionsToDelete.push(block.position + j);
                }
            }
        }

        var idsToDelete = [];
        _.each(positionsToDelete, (pos) => {
            var existingChannel = _.find(this.con['channels'], {channel_position: pos});
            if (existingChannel) idsToDelete.push(existingChannel['channel_id']);
        });

        return {create: channelsToCreate, update: channelsToUpdate, 'delete': idsToDelete};
    };

    getConfigV1() {
        var config = [];

        // Create Settings. Settings have instruction type 0
        config.push({sensor_id: this.sensorId, type: 'INSTR_TYPE', value: 0});
        for (var key in this.cachedUIProcessedConfig.settings) {
            config.push({
                sensor_id: this.sensorId,
                type: key,
                value: this.cachedUIProcessedConfig.settings[key]
            })
        }

        // Add 31 spaces after the settings
        for (var i = 0; i < 31; i++) config.push({sensor_id: this.sensorId, type: 'SPACE', value: 0})

        // Add blocks. We can fit up to 9 blocks in one packet. Each packet has one instruction type and
        // ends with 3 space records
        var blocksPerPacket = 9;

        var blocks = this.cachedUIProcessedConfig.blocks;
        for (var j = 0; j < blocks.length; j++) {
            var block = blocks[j];

            if (j % blocksPerPacket === 0) {
                config.push({sensor_id: this.sensorId, type: 'INSTR_TYPE', value: 1})
            }

            // Add a block
            config.push({
                sensor_id: this.sensorId,
                type: 'ADDR',
                value: block.deviceAddress
            }, {
                sensor_id: this.sensorId,
                type: 'PROPERTY',
                value: block.property
            }, {
                sensor_id: this.sensorId,
                type: 'OBJECTID',
                value: block.objectId
            });

            if (j % blocksPerPacket === (blocksPerPacket - 1)) {
                config.push(
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0}
                )
            }
        }

        return config
    }

    getConfigV2() {
        var blocks = this.cachedUIProcessedConfig.blocks;

        // Build a Lookup table
        var lookupTable: LookupTableEntry[] = [];
        for (var j = 0; j < blocks.length; j++) {
            var block = blocks[j];

            var lookup = {
                macAddress: block.macAddress,
                networkNumber: block.networkNumber,
                deviceAddress: block.deviceAddress
            };

            // If the lookup doesn't exist add it
            if (_.findIndex(lookupTable, lookup) === -1) lookupTable.push(lookup);
        }

        var config = [];
        // Create Settings. Settings have instruction type 0
        config.push({sensor_id: this.sensorId, type: 'INSTR_TYPE', value: 0});
        for (var key in this.cachedUIProcessedConfig.settings) {
            config.push({
                sensor_id: this.sensorId,
                type: key,
                value: this.cachedUIProcessedConfig.settings[key]
            })
        }

        // Add 31 spaces after the settings
        for (var i = 0; i < 31; i++) config.push({sensor_id: this.sensorId, type: 'SPACE', value: 0})

        // Create the Lookup table configuration and keep track of the number of 16bit records
        var lookupBlocksPerPacket = 7;
        var _16bitRecords = 0;
        for (var j = 0; j < lookupTable.length; j++) {
            var b = lookupTable[j];

            if (j % lookupBlocksPerPacket === 0) {
                _16bitRecords = 1;
                config.push({sensor_id: this.sensorId, type: 'INSTR_TYPE', value: 1})
            }

            // Add a block
            config.push({
                sensor_id: this.sensorId,
                type: 'LT_ADDR',
                value: b.deviceAddress
            }, {
                sensor_id: this.sensorId,
                type: 'LT_NETWORK',
                value: b.networkNumber
            }, {
                sensor_id: this.sensorId,
                type: 'LT_MAC',
                value: parseInt(b.macAddress, 16)
            });
            _16bitRecords += 5;

            if (j % lookupBlocksPerPacket === (lookupBlocksPerPacket - 1)) {
                config.push(
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0}
                );

                _16bitRecords += 4;
            }
        }

        // Fill up the packet lookup table packet with spaces
        while (_16bitRecords < 40) {
            config.push({sensor_id: this.sensorId, type: 'SPACE', value: 0});
            _16bitRecords++;
        }

        // Create Block configuration
        var blocksPerPacket = 9;
        for (var j = 0; j < blocks.length; j++) {
            var block = blocks[j];

            if (j % blocksPerPacket === 0) {
                config.push({sensor_id: this.sensorId, type: 'INSTR_TYPE', value: 2})
            }

            // Add a block
            config.push({
                sensor_id: this.sensorId,
                type: 'INDEX',
                value: _.findIndex(lookupTable, {
                    deviceAddress: block.deviceAddress,
                    networkNumber: block.networkNumber,
                    macAddress: block.macAddress
                }) + 1
            }, {
                sensor_id: this.sensorId,
                type: 'PROPERTY',
                value: block.property
            }, {
                sensor_id: this.sensorId,
                type: 'OBJECTID',
                value: block.objectId
            });

            if (j % blocksPerPacket === (blocksPerPacket - 1)) {
                config.push(
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0},
                    {sensor_id: this.sensorId, type: 'SPACE', value: 0}
                )
            }
        }

        return config;
    }

    getConfig() {
        return this.con.sensor_version === 1 ? this.getConfigV1() : this.getConfigV2();
    }

    getUpdatedChannelParams() {
        // Bacnet Currently does not support channel parameters
        return {create: [], update: {}, 'delete': []};
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/settings/sensor_config/BacnetConfigView.ts